import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AxiosInstance from "../../../../services/Api";
import { useSelector } from "react-redux";
import styles from "../academicConfiguration/AcademicCreate.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import { FaCalendarAlt } from "react-icons/fa";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const GroupCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const groupId = location.state?.id || null; // If editing
  const { translations } = useSelector((state) => state.language);

  const [subjectOptions, setSubjectOptions] = useState([]); // Dropdown options
  const [formData, setFormData] = useState({
    name: "",
    subjects: [], // Array of selected objects {label, value}
  });
  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  /** Load all subjects for dropdown */
  const loadSubjects = async () => {
    try {
      const res = await AxiosInstance.post("/school/listSubject", {
        page: "",
        limit: "",
        search: "",
      });

      if (res.data && res.data.rows) {
        const options = res.data.rows.map((subj) => ({
          label: subj.subject_name,
          value: parseInt(subj.id), // Ensure integer
        }));
        setSubjectOptions(options);
      }
    } catch (err) {
      console.error("Error loading subjects:", err);
    }
  };

  /** Load group data for edit */
  const loadGroupData = async (id) => {
    try {
      const res = await AxiosInstance.get(`/school/viewGroup/${id}`);
      if (res.data) {
        const selectedIds = res.data.subject_id
          ? res.data.subject_id.split(",").map((i) => parseInt(i.trim()))
          : [];

        // Set group name (subjects will be set later after options are loaded)
        setFormData((prev) => ({
          ...prev,
          name: res.data.name || "",
        }));

        return selectedIds; // return selected subject IDs for mapping
      }
      return [];
    } catch (err) {
      console.error("Error loading group data:", err);
      return [];
    }
  };

  // Load subjects and then set selected subjects if editing
  useEffect(() => {
    const init = async () => {
      await loadSubjects(); // load dropdown options first
    };
    init();
  }, []);

  // After subjectOptions are loaded, set selected subjects for edit
  useEffect(() => {
    if (groupId && subjectOptions.length > 0) {
      const setSelectedSubjects = async () => {
        const selectedIds = await loadGroupData(groupId);
        const selectedSubjects = subjectOptions.filter((subj) =>
          selectedIds.includes(subj.value),
        );
        setFormData((prev) => ({
          ...prev,
          subjects: selectedSubjects,
        }));
      };
      setSelectedSubjects();
    }
  }, [subjectOptions, groupId]);

  /** Handle input change */
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /** Handle dropdown change */
  const handleDropdownChange = (name, selectedOptions) => {
    setFormData({ ...formData, [name]: selectedOptions || [] });
  };

  /** Form validation */
  const validate = () => {
    const tempErrors = {};
    if (!formData.name) tempErrors.name = "Group name is required";
    if (!formData.subjects || formData.subjects.length === 0)
      tempErrors.subjects = "Select at least one subject";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  /** Delete handler */
  const handleDelete = async () => {
    try {
      const response = await AxiosInstance.delete(
        `/school/deleteGroup/${groupId}`,
      );

      // Check the response status
      if (response?.data?.status === true) {
        // Success case
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || "Group deleted successfully!",
          redirectPath: "/user/GroupList",
        });
      } else {
        // Failed case with custom message from API
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            "Failed to delete group. Please try again.",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error deleting group:", err);
      setDeleteModalOpen(false);

      // Handle error response from API (like 400, 500, etc.)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete group. Please try again.";

      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
    }
  };

  /** Submit handler */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        name: formData.name,
        subject_id: formData.subjects.map((s) => s.value),
      };

      let response;

      if (groupId) {
        response = await AxiosInstance.post("school/updateGroup", {
          ...payload,
          id: groupId,
          updated_by: 102,
        });
      } else {
        response = await AxiosInstance.post("school/createGroup", {
          ...payload,
          created_by: 101,
        });
      }

      // Check response status
      if (response?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            (groupId
              ? "Group updated successfully!"
              : "Group created successfully!"),
          redirectPath: "/user/GroupList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message || "Operation failed. Please try again.",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error saving group:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      {/* Success/Error Modal */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* Delete Confirmation Modal */}
      <PopupModal
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          translations?.labels?.confirmDelete ||
          "Are you sure you want to delete?"
        }
        onOk={handleDelete}
        onClose={() => setDeleteModalOpen(false)}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {groupId ? "Edit Group" : "Create Group"}
          </h1>
        </div>

        <div className="row">
          {/* Subject Dropdown */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.subjects}
              name="subjects"
              value={formData.subjects}
              onChange={(value) => handleDropdownChange("subjects", value)}
              options={subjectOptions}
              placeholder={translations?.labels?.subjectPlaceholder}
              required
              error={errors.subjects}
              isMulti={true}
              closeMenuOnSelect={false}
            />
          </div>

          {/* Group Name Input */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations?.labels?.groupName}
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={translations?.labels?.groupPlaceholder}
              error={errors.name}
              required
            />
          </div>

          {/* Buttons: Delete Group | Cancel | Update/Create Group */}
          <div className="d-flex justify-content-center mt-4 gap-3">
            {/* Delete Group - only in edit mode */}
            {groupId && (
              <CustomButton
                type="button"
                variant="contained"
                fullWidth={false}
                onClick={() => setDeleteModalOpen(true)}
                style={{
                  backgroundColor: "#dc3545",
                  borderColor: "#dc3545",
                  color: "#fff",
                }}
              >
                {translations?.labels?.deletegroup}
              </CustomButton>
            )}

            {/* Cancel */}
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations?.labels?.cancelButton}
            </CustomButton>

            {/* Update / Create */}
            <CustomButton
              onClick={handleSubmit}
              type="button"
              variant="contained"
              fullWidth={false}
              style={{
                backgroundColor: "#ff7a18",
                borderColor: "#ff7a18",
                color: "#fff",
              }}
            >
              {groupId
                ? translations?.labels?.updateButton
                : translations?.labels?.createButton}
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupCreate;
